import { getPermissions } from "@/common/Permissions";

type BasePermissions = ReturnType<typeof getPermissions>;

export type FocusedRolePermissions = BasePermissions & {
  canReadMedicationDispense?: boolean;
  canWriteMedicationDispense?: boolean;
  isPharmacist?: boolean;
  canReadServiceRequest?: boolean;
  canWriteServiceRequest?: boolean;
  canReadDiagnosticReport?: boolean;
  canWriteDiagnosticReport?: boolean;
  canReadSpecimen?: boolean;
  canWriteSpecimen?: boolean;
  canReadSupplyRequest?: boolean;
  canWriteSupplyRequest?: boolean;
  canReadSupplyDelivery?: boolean;
  canWriteSupplyDelivery?: boolean;
  canReadInventoryItem?: boolean;
  canReadProductKnowledge?: boolean;
  canWriteProductKnowledge?: boolean;
  canReadProduct?: boolean;
  canWriteProduct?: boolean;
  canReadChargeItemDefinition?: boolean;
};

export type FocusedServiceKind = "pharmacy" | "inventory";

export function normalizeFocusedService(
  focus?: string | null,
): FocusedServiceKind | null {
  if (!focus) {
    return null;
  }

  const normalized = decodeURIComponent(focus).split(/[?&]/)[0];
  if (normalized === "pharmacy" || normalized === "inventory") {
    return normalized;
  }

  return null;
}

export function appendFocusedQuery(path: string, focus?: string | null) {
  const normalizedFocus = normalizeFocusedService(focus);
  if (!normalizedFocus) {
    return path;
  }

  return `${path}${path.includes("?") ? "&" : "?"}focus=${normalizedFocus}`;
}

function isNarrowServiceBillingProfile(permissions: FocusedRolePermissions) {
  return (
    !permissions.canCreatePatient &&
    !permissions.canWritePatient &&
    !permissions.canCreateEncounter &&
    !permissions.canWriteEncounter &&
    !permissions.canViewAppointments &&
    !permissions.canListUsers &&
    !permissions.canWriteHealthcareService
  );
}

function hasPharmacyWorkflowCore(permissions: FocusedRolePermissions) {
  return (
    permissions.canReadMedicationDispense ||
    permissions.canWriteMedicationDispense ||
    permissions.isPharmacist
  );
}

function hasInventoryWorkflowCore(
  permissions: FocusedRolePermissions,
  options: { includeReadOnlySupport?: boolean } = {},
) {
  const readOnlyInventorySupport =
    options.includeReadOnlySupport &&
    (permissions.canReadInventoryItem ||
      permissions.canReadSupplyRequest ||
      permissions.canReadSupplyDelivery);

  return (
    readOnlyInventorySupport ||
    permissions.canWriteSupplyRequest ||
    permissions.canWriteSupplyDelivery ||
    permissions.canWriteProduct ||
    permissions.canWriteProductKnowledge
  );
}

export function hasLaboratoryWorkflowCore(permissions: FocusedRolePermissions) {
  return (
    permissions.canReadSpecimen ||
    permissions.canWriteSpecimen ||
    ((permissions.canReadServiceRequest ||
      permissions.canWriteServiceRequest) &&
      (permissions.canReadDiagnosticReport ||
        permissions.canWriteDiagnosticReport))
  );
}

export function classifyFocusedServiceRole(
  permissions: FocusedRolePermissions,
): FocusedServiceKind | null {
  if (!isNarrowServiceBillingProfile(permissions)) {
    return null;
  }

  const hasPharmacyCore = hasPharmacyWorkflowCore(permissions);
  const hasInventoryCore = hasInventoryWorkflowCore(permissions, {
    includeReadOnlySupport: !hasPharmacyCore,
  });
  const hasLaboratoryCore = hasLaboratoryWorkflowCore(permissions);

  if (hasPharmacyCore && !hasInventoryCore && !hasLaboratoryCore) {
    return "pharmacy";
  }

  if (hasInventoryCore && !hasPharmacyCore && !hasLaboratoryCore) {
    return "inventory";
  }

  return null;
}

export function hasFocusedPharmacyAccess(permissions: FocusedRolePermissions) {
  return (
    hasPharmacyWorkflowCore(permissions) || permissions.canAccessPharmacyBilling
  );
}

export function hasFocusedInventoryAccess(permissions: FocusedRolePermissions) {
  return (
    hasInventoryWorkflowCore(permissions, { includeReadOnlySupport: true }) ||
    permissions.canReadResourceCategory ||
    permissions.canReadChargeItemDefinition
  );
}

export function hasFocusedLaboratoryAccess(
  permissions: FocusedRolePermissions,
) {
  return (
    hasLaboratoryWorkflowCore(permissions) ||
    permissions.canAccessDiagnosticBilling
  );
}

export function hasFocusedPharmacyRole(permissions: FocusedRolePermissions) {
  return classifyFocusedServiceRole(permissions) === "pharmacy";
}

export function hasFocusedInventoryRole(permissions: FocusedRolePermissions) {
  return classifyFocusedServiceRole(permissions) === "inventory";
}

export function hasFocusedLaboratoryRole(_permissions: FocusedRolePermissions) {
  return false;
}

export function isFocusedServiceBillingRole(
  permissions: FocusedRolePermissions,
) {
  return (
    hasFocusedPharmacyRole(permissions) ||
    hasFocusedInventoryRole(permissions) ||
    hasFocusedLaboratoryRole(permissions)
  );
}
